import React from 'react'

import { Box, Stack, Typography } from '@mui/material'

/**
 * The two label/value primitives used by internal detail pages (Order, Invoice).
 *
 * Extracted from OrderDetailPage on their second use (working-conventions §36 —
 * a second hand-roll becomes a shared component) so the detail surfaces can't
 * drift apart on label typography, gaps, or value alignment.
 */

interface DetailFieldProps {
  label: string
  children: React.ReactNode
}

/**
 * Stacked label-over-value field for a detail/summary column. The label→value
 * gap matches the FieldLabel/InfoItem 8px standard (spacing=2 on the 4px base,
 * §31).
 *
 * The value box is pinned to a 56px min-height so the first line of every value
 * lands at the same y ACROSS COLUMNS. Without it the box just wrapped its
 * content — 24px for plain text but 48–56px for an avatar row (CompanyCell /
 * UserCell / AddressPreview) — and since the content is vertically centred, the
 * text in a tall cell sat ~11px lower than its plain-text neighbour even though
 * the labels lined up. 56 is exactly what the company Order page's `InfoItem`
 * yields (minHeight 84 − label 20 − gap 8), so the two surfaces now share one
 * rhythm.
 */
export const DetailField = ({ label, children }: DetailFieldProps) => (
  <Stack spacing={2}>
    <Typography variant="body3" color="text.secondary">
      {label}
    </Typography>
    <Box display="flex" alignItems="center" sx={{ minHeight: 56 }}>
      {typeof children === 'string' ? (
        <Typography variant="body2">{children}</Typography>
      ) : (
        children
      )}
    </Box>
  </Stack>
)

/**
 * Label-left / value-right row for a summary's money + document lines (shipping
 * price, subtotal, discount, invoice link). Use instead of `DetailField` when
 * the value is a short scalar that reads better aligned in a second column —
 * matching the Total row that closes these summary cards.
 */
export const DetailSummaryRow = ({ label, children }: DetailFieldProps) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ minHeight: 24 }}>
    <Typography variant="body3" color="text.secondary">
      {label}
    </Typography>
    {typeof children === 'string' ? <Typography variant="body2">{children}</Typography> : children}
  </Stack>
)
